/**
 * UranIA PA — Cutover schedule config (US-008 Fase 6).
 *
 * Configurazione frozen della finestra cutover finale per tenant. La
 * data + tenant target sono cementati qui per la 2ª AdG (Regione
 * Autonoma della Sardegna) coerentemente con:
 *   - `scripts/ralph/prd.json` US-008 + US-011 (cutover DNS finale);
 *   - `tasks/prd-fase-6-onboarding-2-adg.md` §6.2 (banner T-7gg, email
 *     T-14gg, status page pubblica);
 *   - `tests/unit/test_cutover_announcement_2adg_pilota.py` (costanti
 *     canoniche `CUTOVER_AT_ISO` allineate letteralmente).
 *
 * Il config qui sotto è statico per Fase 6: cabling dinamico (API
 * `/api/v1/cutover/announcements/active`) arriva in Fase 7. Pattern
 * coerente con altri config statici Fase 6 SPA (es. `cutover-gates`).
 */

export interface CutoverScheduleEntry {
  /** ULID del tenant target (allineato a backend `tenants.tenant_id`). */
  readonly tenantId: string;
  /** Etichetta umana del tenant (es. "Regione Autonoma della Sardegna"). */
  readonly tenantLabel: string;
  /** ISO 8601 UTC della finestra cutover (es. "2027-12-11T06:00:00+00:00"). */
  readonly cutoverAtIso: string;
  /** Etichetta umana della finestra (es. "sabato 11 dicembre 2027, 06:00-10:00 UTC"). */
  readonly cutoverWindowLabel: string;
  /** URL pubblico della status page (US-008 AC#3). */
  readonly statusPageUrl: string;
  /** Alias supporto 2ª AdG (helpdesk dedicato, AC#3 emergency contacts). */
  readonly helpdeskEmail: string;
}

/**
 * Tenant ULID stub canonico per la 2ª AdG (Sardegna), allineato a
 * `tests/unit/test_*_2adg_pilota.py` (TENANT_2ADG=01J0TENANT2ADG00000000000).
 */
export const TENANT_2ADG = '01J0TENANT2ADG00000000000';

/**
 * Tabella cutover Fase 6 (US-008 + US-011). Banner SPA T-7gg si attiva
 * quando `now() ∈ [cutoverAt - 7gg, cutoverAt + 0gg)` per il tenant
 * dell'utente loggato.
 */
export const CUTOVER_SCHEDULE: readonly CutoverScheduleEntry[] = Object.freeze([
  Object.freeze({
    tenantId: TENANT_2ADG,
    tenantLabel: 'Regione Autonoma della Sardegna (PR FESR Sardegna 2021-2027)',
    cutoverAtIso: '2027-12-11T06:00:00+00:00',
    cutoverWindowLabel: 'sabato 11 dicembre 2027, 06:00-10:00 UTC',
    statusPageUrl: 'https://status.urania.eu/cutover-2adg-2027-q4',
    helpdeskEmail: 'helpdesk-sardegna@urania-pa.local',
  }),
]);

/**
 * Numero di giorni PRIMA del cutover dal quale il banner sticky deve
 * apparire (US-008 AC#1 letterale: "starts 7 days before cutover").
 */
export const CUTOVER_BANNER_LEAD_DAYS = 7;

/**
 * Numero di giorni PRIMA del cutover dal quale viene emessa l'email
 * DPO + Sponsor (US-008 AC#2 letterale: "14 days before"). Riferimento
 * per documentazione + runbook PM cutover; non usato dal banner UI ma
 * citato nei test di handler backend per coerenza.
 */
export const CUTOVER_EMAIL_LEAD_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Calcola i giorni intero ceil-up rimanenti al cutover. Negativi se
 * il cutover è già passato. Ceil-up perché "Cutover in {N} giorni"
 * tipicamente arrotonda per eccesso (es. 0.4gg residui ⇒ "in 1 giorno").
 */
export function daysUntilCutover(cutoverAtIso: string, now: Date): number {
  const cutoverAtMs = Date.parse(cutoverAtIso);
  if (Number.isNaN(cutoverAtMs)) {
    return Number.POSITIVE_INFINITY;
  }
  const deltaMs = cutoverAtMs - now.getTime();
  return Math.ceil(deltaMs / MS_PER_DAY);
}

/**
 * Ritorna l'entry CutoverSchedule per il tenant `tenantId` se presente,
 * altrimenti `null`. Helper usato dal banner per recuperare il config
 * relativo all'utente loggato.
 */
export function findCutoverEntryForTenant(tenantId: string | null): CutoverScheduleEntry | null {
  if (tenantId === null) {
    return null;
  }
  return CUTOVER_SCHEDULE.find((e) => e.tenantId === tenantId) ?? null;
}

/**
 * Decide se il banner deve essere visibile per il tenant + clock dato.
 * Visibile sse:
 *   - esiste entry per il tenant;
 *   - 0 < daysUntil <= CUTOVER_BANNER_LEAD_DAYS (banner non visibile
 *     dopo il cutover);
 *   - `daysUntil === 0` (giorno del cutover) visibile come "oggi".
 *
 * Returns `{ entry, daysUntil }` se visibile, `null` altrimenti.
 */
export function resolveActiveCutoverForTenant(
  tenantId: string | null,
  now: Date,
): { readonly entry: CutoverScheduleEntry; readonly daysUntil: number } | null {
  const entry = findCutoverEntryForTenant(tenantId);
  if (entry === null) {
    return null;
  }
  const daysUntil = daysUntilCutover(entry.cutoverAtIso, now);
  if (daysUntil < 0 || daysUntil > CUTOVER_BANNER_LEAD_DAYS) {
    return null;
  }
  return { entry, daysUntil };
}
